/**
 * AdminActiveSessions.js
 *
 * Tüm kullanıcıların aktif oturumlarını listeler.
 * Filtre: kullanıcı adı/e-posta, platform (ios/android/web), ülke (ip_country).
 * Aksiyon: "Tüm Oturumları Sonlandır" (kullanıcı bazlı, 2-adım onay).
 *
 * Route: /admin/active-sessions
 */

import { useState, useDeferredValue } from 'react';
import { motion } from 'framer-motion';
import { Shield, Monitor, Smartphone, Globe, RefreshCw, LogOut } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  DataTable,
  FilterBar,
  PageHeader,
  ActionMenu,
  EmptyState,
  LoadingState,
} from '../components/admin';
import DangerousConfirmDialog from '@/components/admin/DangerousConfirmDialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { useActiveSessions, useRevokeUserSessions } from '../api/security';

// ── Platform icon helper ──────────────────────────────────────────────────────

function PlatformBadge({ platform }) {
  const icons = {
    ios: <Smartphone size={13} className="inline mr-1" />,
    android: <Smartphone size={13} className="inline mr-1" />,
    web: <Monitor size={13} className="inline mr-1" />,
  };
  const colors = {
    ios: 'bg-blue-50 text-blue-600',
    android: 'bg-green-50 text-green-600',
    web: 'bg-purple-50 text-purple-600',
  };
  const key = (platform || 'web').toLowerCase();
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${colors[key] || 'bg-gray-50 text-gray-500'}`}>
      {icons[key] || <Globe size={13} className="inline mr-1" />}
      {(platform || 'web').toUpperCase()}
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminActiveSessions() {
  const { t, lang } = useLanguage();
  const toast = useToast();

  // Filters
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('');
  const [ipCountry, setIpCountry] = useState('');
  const [page, setPage] = useState(0);
  const LIMIT = 20;

  const deferredSearch = useDeferredValue(search);

  // Revoke confirm state
  const [revokeTarget, setRevokeTarget] = useState(null); // { userId, userEmail }
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);

  // Data
  const { data, isLoading, isFetching, refetch } = useActiveSessions({
    search: deferredSearch,
    platform,
    ip_country: ipCountry,
    page,
    limit: LIMIT,
  });

  const sessions = data?.sessions || [];
  const total = data?.total || 0;

  // Mutation
  const revokeAll = useRevokeUserSessions();

  const handleRevokeOpen = (row) => {
    setRevokeTarget({ userId: row.user_id, userEmail: row.user_email });
    setRevokeDialogOpen(true);
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    try {
      await revokeAll.mutateAsync(revokeTarget.userId);
      toast.success(
        lang === 'tr'
          ? `${revokeTarget.userEmail} — tüm oturumlar sonlandırıldı`
          : `${revokeTarget.userEmail} — all sessions revoked`
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setRevokeDialogOpen(false);
      setRevokeTarget(null);
    }
  };

  // DataTable columns
  const columns = [
    {
      key: 'user_email',
      label: t('admin.security.activeSessions.colUser'),
      sortable: true,
      render: (_val, row) => (
        <div className="min-w-0">
          <p className="text-xs font-bold text-[var(--text-main)] truncate">
            {row.user_name || '—'}
          </p>
          <p className="text-[10px] text-[var(--text-muted)] truncate">{row.user_email}</p>
        </div>
      ),
    },
    {
      key: 'device_name',
      label: t('admin.security.activeSessions.colDevice'),
      render: (_val, row) => (
        <span className="text-xs text-[var(--text-main)]">{row.device_name || '—'}</span>
      ),
    },
    {
      key: 'platform',
      label: t('admin.security.activeSessions.colPlatform'),
      render: (_val, row) => <PlatformBadge platform={row.platform} />,
    },
    {
      key: 'ip_address',
      label: t('admin.security.activeSessions.colIp'),
      render: (_val, row) => (
        <span className="font-mono text-[11px] text-[var(--text-muted)]">
          {row.ip_address || '—'}
          {row.ip_country ? (
            <span className="ml-1 text-[9px] uppercase opacity-60">[{row.ip_country}]</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'last_seen_at',
      label: t('admin.security.activeSessions.colLastSeen'),
      sortable: true,
      render: (_val, row) => (
        <span className="text-xs text-[var(--text-muted)]">
          {row.last_seen_at
            ? new Date(row.last_seen_at).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')
            : '—'}
        </span>
      ),
    },
    {
      key: '_actions',
      label: '',
      render: (_val, row) => (
        <ActionMenu
          items={[
            {
              label: t('admin.security.activeSessions.actionRevoke'),
              icon: <LogOut size={14} />,
              variant: 'danger',
              onClick: () => handleRevokeOpen(row),
            },
          ]}
        />
      ),
    },
  ];

  // FilterBar config
  const filterFields = [
    {
      type: 'search',
      key: 'search',
      placeholder: t('admin.security.activeSessions.filterSearch'),
      value: search,
      onChange: (v) => { setSearch(v); setPage(0); },
    },
    {
      type: 'select',
      key: 'platform',
      label: t('admin.security.activeSessions.filterPlatform'),
      value: platform,
      onChange: (v) => { setPlatform(v); setPage(0); },
      options: [
        { value: '', label: t('admin.filter_all') },
        { value: 'ios', label: 'iOS' },
        { value: 'android', label: 'Android' },
        { value: 'web', label: 'Web' },
      ],
    },
    {
      type: 'text',
      key: 'ip_country',
      label: t('admin.security.activeSessions.filterCountry'),
      placeholder: 'TR, US...',
      value: ipCountry,
      onChange: (v) => { setIpCountry(v.toUpperCase()); setPage(0); },
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-4">
        <PageHeader
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            { label: t('admin.nav.system') },
            { label: t('admin.nav.activeSessions') },
          ]}
          title={t('admin.security.activeSessions.title')}
          subtitle={t('admin.security.activeSessions.desc')}
          icon={<Shield size={20} />}
          actions={
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 border-[var(--border-light)] text-xs font-bold text-[var(--text-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
              {lang === 'tr' ? 'Yenile' : 'Refresh'}
            </motion.button>
          }
        />

        <FilterBar fields={filterFields} />

        {isLoading ? (
          <LoadingState message={t('admin.security.activeSessions.loading')} />
        ) : sessions.length === 0 ? (
          <EmptyState
            title={t('admin.security.activeSessions.emptyTitle')}
            description={t('admin.security.activeSessions.emptyDesc')}
            icon={<Shield size={32} />}
          />
        ) : (
          <DataTable
            columns={columns}
            data={sessions}
            pageSize={LIMIT}
            serverPagination
            currentPage={page + 1}
            totalItems={total}
            onPageChange={(p) => setPage(p - 1)}
          />
        )}
      </div>

      {/* 2-step confirm dialog */}
      <DangerousConfirmDialog
        open={revokeDialogOpen}
        onOpenChange={setRevokeDialogOpen}
        count={1}
        onConfirm={handleRevokeConfirm}
        loading={revokeAll.isPending}
      />
    </AdminLayout>
  );
}
